import { useParams } from "react-router";
import UrlForm from "../Pages/Wishlist/UrlForm";
import Error from "../Pages/Error";
import EditForm from "../Pages/Wishlist/EditForm";
import { makeItem, WishlistItem } from "../../data/types";
import { isValidURL } from "../../data/strings";
import { parse } from "../../data/parser";
import { Form, FormInstance } from "antd";
import { useEffect, useState } from "react";
import { updateWish } from "../../data/firestore/wishlist";
import { useDispatch } from "react-redux";
import { loadData, setIsLoading as setIsLoadingAction } from "../../data/redux/wishlist";

interface WizardSecrets {
  form: FormInstance<WishlistItem>;
  wishData?: WishlistItem;
  setWishData: (val: WishlistItem | undefined) => void;
}

interface Props {
  isLoading: boolean;
  setIsLoading: (val: boolean) => void;
  onSubmit: () => void;
  wizardSecrets: WizardSecrets;
}

type SetBoolean = (v: boolean) => void;
type FnVoid = () => void;

export function useWishCreateWizard(): [
  boolean,
  SetBoolean,
  FnVoid,
  FnVoid,
  boolean,
  WizardSecrets,
] {
  const [wishData, setWishData] = useState<WishlistItem | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();

  const submitForm = () => {
    form.submit();
  };

  const cleanup = () => {
    setIsLoading(false);
    setWishData(undefined);
    form.resetFields();
  };

  const isFinalStep = !!wishData;

  const wizardSecrets: WizardSecrets = {
    form,
    wishData,
    setWishData,
  };

  return [
    isLoading,
    setIsLoading,
    submitForm,
    cleanup,
    isFinalStep,
    wizardSecrets,
  ];
}

export default function WishCreateWizard({
  isLoading,
  setIsLoading,
  onSubmit,
  wizardSecrets,
}: Props) {
  const { form, wishData, setWishData } = wizardSecrets;

  const { wishlistId } = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    if (wishData) {
      form.resetFields();
    }
  }, [wishData]);

  if (!wishlistId) {
    return <Error message="Wishlist not found" />;
  }

  const onUrlSubmitted = async (url: string) => {
    const emptySuggestion: WishlistItem = makeItem();

    if (!isValidURL(url)) {
      setWishData({ ...emptySuggestion, name: url });
      return;
    }
    setIsLoading(true);
    try {
      const product = await parse(url);
      if (product.price === "NaN") {
        product.price = "";
      }
      setWishData({
        ...emptySuggestion,
        name: product.title,
        description: product.description,
        link: url,
        imageLink: product.imageURL,
        price: product?.price || "",
        currency: product.currency,
      });
    } catch (e) {
      console.error("Parsing error: ", e);
      setWishData({ ...emptySuggestion, link: url });
    } finally {
      setIsLoading(false);
      onSubmit();
    }
  };

  const onEditSubmitted = async (formData: WishlistItem) => {
    onSubmit();
    setWishData(undefined);
    dispatch(setIsLoadingAction());
    const newWishlist = await updateWish(wishlistId, formData);
    dispatch(loadData({ wishlists: [newWishlist] }));
  };

  return (
    <>
      <>
        {!wishData && (
          <UrlForm
            onSubmit={onUrlSubmitted}
            isLoading={isLoading}
            form={form}
          />
        )}
        {wishData && (
          <EditForm
            wishlistId={wishlistId}
            wish={wishData}
            isLoading={isLoading}
            form={form}
            onSubmit={onEditSubmitted}
            showDataInfo
          />
        )}
      </>
    </>
  );
}
